import React, { useCallback, useEffect, useState } from "react";
import {
  getPurchaseRates,
  savePurchaseRates,
  copyPurchaseRateGrade,
} from "../../api/service";

const RUPEE = "\u20B9";
const SUBS = [
  { value: "", label: "All items" },
  { value: "plywood", label: "Plywood" },
  { value: "inner_laminate", label: "Inner laminate" },
  { value: "accessories", label: "Accessories" },
];

export default function RateListTab({ opts }) {
  const grades = opts.material_grades || [];
  const [grade, setGrade] = useState(grades.length ? grades[0].value : "");
  const [subType, setSubType] = useState("");
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [missing, setMissing] = useState(false);
  const [data, setData] = useState({ results: [], total: 0, filled: 0, matched: 0 });
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    const h = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(h);
  }, [qInput]);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (grade) params.grade = grade;
    if (subType) params.sub_type = subType;
    if (q) params.q = q;
    if (missing) params.missing = "1";
    return getPurchaseRates(params)
      .then((res) => {
        setData(res);
        setErr("");
      })
      .catch((e) => setErr(e.message || "Could not load rates."))
      .finally(() => setLoading(false));
  }, [grade, subType, q, missing]);

  useEffect(() => {
    load();
  }, [load]);

  const setEdit = (r, v) => {
    if (!/^\d*\.?\d{0,2}$/.test(v)) return;
    setEdits((e) => {
      const next = { ...e };
      if (v === (r.price || "")) delete next[r.id];
      else next[r.id] = v;
      return next;
    });
  };

  const editCount = Object.keys(edits).length;

  const save = async () => {
    if (!editCount) return;
    setBusy(true);
    setMsg("");
    try {
      const rates = Object.keys(edits).map((id) => ({ id: Number(id), price: edits[id] }));
      const res = await savePurchaseRates({ rates });
      setMsg(res.updated + " rate(s) saved.");
      setEdits({});
      await load();
    } catch (e) {
      setErr(e.message || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const copyGrade = async () => {
    if (grades.length < 2) return;
    if (editCount && !window.confirm("You have unsaved edits that will stay as they are. Continue?")) return;
    if (!window.confirm("Copy " + grades[0].label + " rates to " + grades[1].label + " wherever blank?")) return;
    setBusy(true);
    setMsg("");
    try {
      const res = await copyPurchaseRateGrade({ from: grades[0].value, to: grades[1].value });
      setMsg(res.copied + " blank rate(s) filled from " + grades[0].label + ".");
      await load();
    } catch (e) {
      setErr(e.message || "Copy failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="d-flex align-items-end justify-content-between flex-wrap gap-2 mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-auto">
            <label className="form-label small mb-1">Material grade</label>
            <select
              className="form-select form-select-sm"
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
            >
              {grades.map((g) => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </div>
          <div className="col-auto">
            <label className="form-label small mb-1">Items</label>
            <select
              className="form-select form-select-sm"
              value={subType}
              onChange={(e) => setSubType(e.target.value)}
            >
              {SUBS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div className="col-auto">
            <label className="form-label small mb-1">Search</label>
            <input
              className="form-control form-control-sm"
              placeholder="e.g. channel 18"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
            />
          </div>
          <div className="col-auto">
            <div className="form-check mb-1">
              <input
                id="rl-missing"
                type="checkbox"
                className="form-check-input"
                checked={missing}
                onChange={(e) => setMissing(e.target.checked)}
              />
              <label htmlFor="rl-missing" className="form-check-label small">
                Missing rates only
              </label>
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="small text-muted">
            {data.filled} of {data.total} rates filled
          </span>
          <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={copyGrade}>
            Copy {grades[0] ? grades[0].label : ""} to {grades[1] ? grades[1].label : ""} (blanks)
          </button>
          <button type="button" className="btn btn-primary btn-sm" disabled={busy || !editCount} onClick={save}>
            Save changes ({editCount})
          </button>
        </div>
      </div>

      {err && <div className="alert alert-danger py-2">{err}</div>}
      {msg && <div className="alert alert-success py-2">{msg}</div>}

      <div className="table-responsive">
        <table className="table table-sm align-middle" style={{ fontSize: 13 }}>
          <thead>
            <tr>
              <th>Item</th>
              <th>Unit</th>
              <th style={{ width: 180 }}>Price ({RUPEE})</th>
            </tr>
          </thead>
          <tbody>
            {(data.results || []).map((r) => {
              const dirty = edits[r.id] !== undefined;
              const value = dirty ? edits[r.id] : r.price || "";
              return (
                <tr key={r.id} className={dirty ? "table-warning" : ""}>
                  <td>{r.label}</td>
                  <td>{r.unit}</td>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      inputMode="decimal"
                      placeholder="not set"
                      value={value}
                      onChange={(e) => setEdit(r, e.target.value)}
                    />
                  </td>
                </tr>
              );
            })}
            {!loading && (data.results || []).length === 0 && (
              <tr>
                <td colSpan={3} className="text-center text-muted py-3">
                  No items match.
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={3} className="text-center text-muted py-3">
                  Loading...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
